import { useMemo } from 'react';
import { DataTable, type Column } from '../DataTable/DataTable';
import { color, font, fontSize } from '../../theme/tokens';
import {
  exactCell,
  formatCell,
  partialBucketLabel,
  rowCountFace,
  PARTIAL_BUCKET_NOTE,
  type ColumnRole,
  type ColumnSpec,
} from './format';

/**
 * The analyst's result table (spec 006 T070/T072/T073).
 *
 * ## ⚠ Built from the RESULT ROWS, never parsed out of the model's Markdown
 *
 * The model emits Markdown prose and it is rendered as Markdown — but a table the reader can
 * sort, export and copy is built from the result set. A model asked to re-type ten rows of
 * figures will eventually mistype one, and a mistyped figure beside correct SQL is
 * indistinguishable from a data problem.
 *
 * ## ⚠ It renders over `<DataTable>`, so gate 2 holds for free
 *
 * That component makes `fullLabel` a required property of every column, which is what makes
 * *"every header shows the short code and carries the full label on hover, no column exempt"*
 * structural rather than a convention someone has to remember for an ad-hoc result.
 */

export interface ResultTableProps {
  columns: readonly ColumnSpec[];
  rows: readonly unknown[][];
  /** ⚠ The TOTAL the query produced, not `rows.length`. */
  totalRows: number;
  /** Index of the partial bucket, for a series result (FR-A21). */
  partialRowIndex?: number;
  /**
   * The result has expired from the store (FR-A80).
   *
   * ⚠ This is the NORMAL state of any thread older than the result TTL, not an edge case.
   */
  expired?: boolean;
  onReask?: () => void;
}

type Row = { readonly cells: readonly unknown[]; readonly index: number };

/*
 * ## ⚠ Tracks are sized from the CONTENT, and a cell is ONE line
 *
 * Every other table in the product knows its columns in advance and sizes them from the
 * formatter. This one does not — an answer can be four columns or twenty-one, a two-letter state
 * beside a sixty-character job title — so `<DataTable>`'s default `minmax(50px, 1fr)` squeezed
 * all of them into equal slivers: a title wrapped to eight lines, and an unbreakable value (a job
 * id, `NURSING`, `Providence`) ran straight across the column beside it. So each track is as wide
 * as its longest face, capped per role, and a face past the cap ends in an ellipsis with the
 * whole value on hover — the row stays one line tall and a reader can scan down a column.
 *
 * The estimate is character count × an advance per face. Mono is exact (600/1000 em); the sans
 * figure is an average, so it is set generously — an over-estimate costs whitespace, an
 * under-estimate costs an ellipsis on a value that would have fitted.
 */
const CELL_FONT_PX = 12; // fontSize.body
const MONO_ADVANCE = CELL_FONT_PX * 0.6;
const SANS_ADVANCE = CELL_FONT_PX * 0.58;
// Bold 9px capitals plus the header's .2px tracking.
const HEADER_ADVANCE = 6.4;
// The sort arrow and its gap, so a sorted header does not push past its own track.
const HEADER_ARROW = 12;
/*
 * Horizontal chrome around a face: `<DataTable>`'s interior gutter (6px each side, via
 * `interiorGutters`) plus this renderer's own 4px, plus 6px of slack for the pinned column's 1px
 * seam and sub-pixel rounding — without it an 8-digit job id lands exactly on its track and loses
 * its last glyph to the ellipsis. The edge columns get 16px on their outer side instead of 6px,
 * hence the extra.
 */
const CELL_CHROME = 26;
const EDGE_EXTRA = 10;
const MIN_TRACK = 52;

/** The widest a track may grow before its faces are cut, by role. */
const MAX_TRACK: Record<ColumnRole, number> = {
  identifier: 200,
  date: 120,
  measure: 120,
  ratio: 120,
  label: 280,
};

/**
 * Figures take the mono face, so a column of them lines up digit for digit.
 *
 * An identifier is mono only when it is a NUMBER: `job_id` is, but `client_key` is an account
 * name, and a name set in mono beside the same name in sans reads as two different things. It is
 * decided once per COLUMN, from every value on screen, so a column never mixes the two faces.
 */
function isMonoColumn(role: ColumnRole, values: readonly unknown[]): boolean {
  if (role === 'measure' || role === 'ratio') return true;
  if (role !== 'identifier') return false;
  return values.every((v) => v === null || v === undefined || v === '' || typeof v === 'number');
}

/**
 * One track's width in px, from the header and every face in the column.
 *
 * Exported for the test alone: the arithmetic is the part that decides whether a value is cut.
 */
export function trackWidth(
  spec: ColumnSpec,
  faces: readonly string[],
  isEdge: boolean,
  mono: boolean,
): number {
  const advance = mono ? MONO_ADVANCE : SANS_ADVANCE;
  const longest = faces.reduce((max, face) => Math.max(max, face.length), 0);
  const body = longest * advance;
  const header = spec.label.length * HEADER_ADVANCE + HEADER_ARROW;
  const chrome = CELL_CHROME + (isEdge ? EDGE_EXTRA : 0);
  const natural = Math.ceil(Math.max(body, header) + chrome);
  return Math.min(MAX_TRACK[spec.role] + chrome, Math.max(MIN_TRACK, natural));
}

export function ResultTable({
  columns,
  rows,
  totalRows,
  partialRowIndex,
  expired,
  onReask,
}: ResultTableProps) {
  /*
   * Measured over the rows ON SCREEN (≤500 of them), once per result — a sort reorders the rows
   * and changes no face, so it must not re-measure or the columns would jump under the click.
   */
  const { widths, fits, mono } = useMemo(() => {
    const mono = columns.map((spec, columnIndex) =>
      isMonoColumn(
        spec.role,
        rows.map((cells) => cells[columnIndex]),
      ),
    );
    const widths = columns.map((spec, columnIndex) => {
      const faces = rows.map((cells) => formatCell(cells[columnIndex], spec.role));
      // The partial bucket's `*` is a glyph like any other.
      if (partialRowIndex !== undefined && columnIndex === 0 && faces[partialRowIndex] !== undefined) {
        faces[partialRowIndex] = partialBucketLabel(faces[partialRowIndex]);
      }
      return trackWidth(
        spec,
        faces,
        columnIndex === 0 || columnIndex === columns.length - 1,
        mono[columnIndex]!,
      );
    });
    // How many glyphs the MINIMUM track holds — past that, the face may be cut and earns a title.
    const fits = columns.map((_spec, columnIndex) => {
      const chrome =
        CELL_CHROME +
        (columnIndex === 0 || columnIndex === columns.length - 1 ? EDGE_EXTRA : 0);
      const advance = mono[columnIndex] ? MONO_ADVANCE : SANS_ADVANCE;
      return Math.floor((widths[columnIndex]! - chrome) / advance);
    });
    return { widths, fits, mono };
  }, [columns, rows, partialRowIndex]);

  /*
   * ⚠ The expired state renders the ANSWER WITHOUT ITS TABLE, plus a way to re-ask — never an
   * empty table, which reads as "the query returned nothing" rather than "these rows are gone".
   * Transcripts hold text only by construction, so this is what a reopened thread looks like
   * once the rows have expired.
   */
  if (expired) {
    return (
      <div
        style={{
          padding: '12px 14px',
          borderRadius: 8,
          background: color.surfaceSubtle,
          color: color.inkMuted,
          fontSize: fontSize.caption,
        }}
      >
        <div>These rows have expired. The answer above is unchanged.</div>
        {onReask ? (
          <button
            type="button"
            onClick={onReask}
            style={{
              marginTop: 8,
              background: 'transparent',
              border: `1px solid ${color.border}`,
              borderRadius: 6,
              padding: '4px 10px',
              color: color.ink,
              cursor: 'pointer',
              fontSize: fontSize.caption,
            }}
          >
            Ask again
          </button>
        ) : null}
      </div>
    );
  }

  // FR-055's logic: a zero-row answer is an empty state with a sentence, not a table of dashes.
  if (rows.length === 0) {
    return (
      <div style={{ padding: '12px 14px', color: color.inkMuted, fontSize: fontSize.caption }}>
        Nothing matched that question.
      </div>
    );
  }

  const tableColumns: Column<Row>[] = columns.map((spec, columnIndex) => ({
    key: spec.key,
    label: spec.label,
    fullLabel: spec.fullLabel,
    align: spec.role === 'measure' || spec.role === 'ratio' ? 'right' : 'left',
    width: `minmax(${widths[columnIndex]}px, ${widths[columnIndex]}fr)`,
    sortable: true,
    render: (row) => {
      const value = row.cells[columnIndex];
      const face = formatCell(value, spec.role);
      const exact = exactCell(value, spec.role);

      /*
       * ⚠ The partial bucket is marked, and the reason travels in the accessible name. A period
       * sum dives at the right edge for no reason but the period being days old; the chart
       * dashes it, and a table that draws it like any other bucket is the two readings of one
       * snapshot disagreeing about the one thing they must agree on.
       */
      const partial = partialRowIndex === row.index && columnIndex === 0;
      const shown = partial ? partialBucketLabel(face) : face;

      /*
       * The whole value on hover — but only where the face could have been cut. A tooltip on
       * every `Yes` and `CA` in a 500-row table is noise that trains a reader to ignore the ones
       * that carry something.
       */
      const hover = partial
        ? `${exact || face} — ${PARTIAL_BUCKET_NOTE}`
        : shown.length > fits[columnIndex]!
          ? exact || face
          : undefined;

      return (
        <div
          // ⚠ Every digit in the accessible name, where the face is compacted.
          aria-label={partial ? `${exact || face}, ${PARTIAL_BUCKET_NOTE}` : exact || undefined}
          title={hover}
          style={{
            // A flex item inside the pinned first column, a block everywhere else — `minWidth: 0`
            // is what lets the ellipsis happen in the former.
            flex: '1 1 auto',
            minWidth: 0,
            padding: '7px 4px',
            fontFamily: mono[columnIndex] ? font.mono : font.sans,
            fontSize: fontSize.body,
            lineHeight: 1.35,
            color: value === null || value === undefined || value === '' ? color.inkMuted : color.ink,
            textAlign: spec.role === 'measure' || spec.role === 'ratio' ? 'right' : 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {shown}
        </div>
      );
    },
    sortValue: (row) => {
      const value = row.cells[columnIndex];
      if (value === null || value === undefined) return null;
      return typeof value === 'number' ? value : String(value);
    },
  }));

  const data: Row[] = rows.map((cells, index) => ({ cells, index }));

  return (
    <div>
      <DataTable
        columns={tableColumns}
        rows={data}
        rowKey={(row) => String(row.index)}
        caption="Analyst result"
        // The sum of the tracks' floors: past it the table scrolls inside its own card rather
        // than squeezing a column below its longest face.
        minWidth={`${widths.reduce((sum, w) => sum + w, 0)}px`}
        interiorGutters
        /*
         * ⚠ **A row answer is now as wide as the view** — 21 columns for a placement, 18 for a
         * job (owner request 2026-09-14) — so the first column has to stay put. Scrolling
         * sideways past `contract_id` leaves a reader looking at rows of unattributed values,
         * which is the same failure the chart matrix opted into this for, one surface over.
         *
         * ⚠ Safe here because this table paints no row backgrounds of its own: the pinned cells
         * take the default surface, which is the condition the prop's own note sets.
         */
        stickyFirstColumn
      />
      {/*
        ⚠ The count is the TOTAL, and the export's promise is stated BEFORE the reader clicks.
        A table headed `500` teaches the reader the answer was small.
      */}
      <div
        style={{
          marginTop: 6,
          fontSize: fontSize.caption,
          color: color.inkMuted,
        }}
      >
        {rowCountFace(totalRows, rows.length)}
      </div>
    </div>
  );
}
